import React, { useEffect, useState } from 'react';
import type { GuardCheckDetailDto, GuardCheckDto, GuardCheckFindingDto, GuardSiteDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { Empty, ErrorBanner, Panel } from '../ui';

const when = (iso: string) => new Date(iso).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const KIND = { automatico: 'Automatico', manuale: 'Manuale' };
const SEVERITY: Record<string, { label: string; tone: 'bad' | 'warn' | 'info' }> = {
  critical: { label: 'Critica', tone: 'bad' },
  high: { label: 'Alta', tone: 'bad' },
  medium: { label: 'Media', tone: 'warn' },
  low: { label: 'Bassa', tone: 'info' },
};

function BackTitle({ text, onBack, hint }: { text: string; onBack: () => void; hint: string }) {
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <button type="button" className="btn icon ghost" title={hint} onClick={onBack}>
        <Icon name="chevronLeft" size={18} />
      </button>
      {text}
    </span>
  );
}

const TABS = [
  { key: 'non_compliant', label: 'Azioni consigliate', tone: 'bad', found: 'Risultato' },
  { key: 'unverifiable', label: 'Non verificabili', tone: 'warn', found: 'Perché non si può verificare' },
  { key: 'compliant', label: 'Superati', tone: 'good', found: 'Risultato' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

/** Una riga chiusa: solo il titolo. Con un clic si apre e mostra il resto. */
function FindingRow({ f, tone, found, open, onToggle }: { f: GuardCheckFindingDto; tone: string; found: string; open: boolean; onToggle: () => void }) {
  const sev = SEVERITY[f.severity ?? ''];
  const dot = f.status === 'non_compliant' ? sev?.tone ?? 'info' : tone;
  return (
    <div className={`ck-item${open ? ' open' : ''}`}>
      <button type="button" className="ck-row" aria-expanded={open} onClick={onToggle}>
        <span className={`ck-dot ${dot}`} />
        <span className="ck-title">{f.title}</span>
        <Icon name="chevronRight" size={16} />
      </button>
      {open && (
        <div className="ck-body">
          {f.summary && (
            <>
              <div className="ck-label">{found}</div>
              <blockquote className={`ck-quote ${dot}`}>{f.summary}</blockquote>
            </>
          )}
          {f.recommendation && f.status !== 'compliant' && (
            <>
              <div className="ck-label">Come intervenire</div>
              <blockquote className="ck-quote todo">{f.recommendation}</blockquote>
            </>
          )}
          {f.explain && (
            <details className="ck-more">
              <summary>Perché è importante</summary>
              <p>{f.explain}</p>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

/** I risultati di una verifica: tre riquadri come filtri, sotto l'elenco per gravità. */
function CheckDetail({ ctx, id, onBack }: { ctx: Ctx; id: number; onBack: () => void }) {
  const [data, setData] = useState<GuardCheckDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabKey>('non_compliant');
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    ctx.api.get<GuardCheckDetailDto>(`/api/guard/checks/${id}`).then(setData).catch((e) => setError((e as Error).message));
  }, [ctx.api, id]);
  const all = data?.findings ?? [];
  const current = TABS.find((t) => t.key === tab)!;
  const shown = all.filter((f) => f.status === tab);
  const groups = tab === 'non_compliant'
    ? ['critical', 'high', 'medium', 'low'].map((k) => ({ key: k, title: `Gravità ${SEVERITY[k].label.toLowerCase()}`, items: shown.filter((f) => f.severity === k) }))
      .concat([{ key: 'other', title: 'Altro', items: shown.filter((f) => !SEVERITY[f.severity ?? '']) }])
    : [{ key: 'all', title: '', items: shown }];
  return (
    <Panel
      title={<BackTitle text={data ? `Verifica del ${when(data.finishedAt)}` : 'Verifica'} onBack={onBack} hint="Torna all’elenco delle verifiche" />}
      subtitle={data ? `${data.pc} · ${KIND[data.kind]}` : undefined}
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!data ? (
        !error && <div className="muted small">Caricamento…</div>
      ) : (
        <div className="ck">
          <div className="ck-tabs" role="tablist" aria-label="Risultati della verifica">
            {TABS.map((t) => (
              <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} className={`ck-tile ${t.tone}${tab === t.key ? ' active' : ''}`}
                onClick={() => { setTab(t.key); setOpen(null); }}>
                <span className="ck-num">{all.filter((f) => f.status === t.key).length}</span>
                <span className="ck-name">{t.label}</span>
              </button>
            ))}
          </div>
          {shown.length === 0 && <div className="muted small">Niente in questo elenco.</div>}
          {groups.filter((g) => g.items.length > 0).map((g) => (
            <section key={g.key} className="ck-group">
              {g.title && <h3 className="ck-group-title">{g.title}<span>{g.items.length}</span></h3>}
              <div className="ck-list">
                {g.items.map((f, i) => {
                  const key = f.id ?? `${g.key}-${i}`;
                  return <FindingRow key={key} f={f} tone={current.tone} found={current.found} open={open === key} onToggle={() => setOpen(open === key ? null : key)} />;
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </Panel>
  );
}

/** Licenze › azienda: se è stata eseguita una verifica, quando, quante volte; con un clic i risultati. */
export function SentinelChecks({ ctx, site, onBack }: { ctx: Ctx; site: GuardSiteDto; onBack: () => void }) {
  const [list, setList] = useState<GuardCheckDto[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    ctx.api.get<{ checks: GuardCheckDto[]; total: number }>(`/api/guard/sites/${site.id}/checks`)
      .then((r) => { setList(r.checks); setTotal(r.total); })
      .catch((e) => setError((e as Error).message));
  }, [ctx.api, site.id]);

  if (open !== null) return <div className="panels single"><CheckDetail ctx={ctx} id={open} onBack={() => setOpen(null)} /></div>;
  const last = list?.[0];
  return (
    <div className="panels single">
      <Panel
        title={<BackTitle text={site.clientName ?? site.name} onBack={onBack} hint="Torna alle licenze" />}
        subtitle={list ? (total === 0 ? 'Nessuna verifica eseguita' : `${total === 1 ? '1 verifica eseguita' : `${total} verifiche eseguite`} · ultima il ${when(last!.finishedAt)}`) : undefined}
        right={<button type="button" className="btn" title="Apre tutta la scheda dell’azienda" onClick={() => ctx.setPage('security', site.id)}>Apri in Monitoraggio</button>}
      >
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {!list ? (
          !error && <div className="muted small">Caricamento…</div>
        ) : list.length === 0 ? (
          <Empty icon="shield">La prima verifica arriva da sola quando Sentinel viene attivato su un PC.</Empty>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Quando</th><th>PC</th><th>Tipo</th><th>Superati</th><th>Azioni consigliate</th><th>Non verificabili</th></tr>
            </thead>
            <tbody>
              {list.map((c) => (
                <tr key={c.id} className="clickable" title="Vedi i risultati" onClick={() => setOpen(c.id)}>
                  <td><b>{when(c.finishedAt)}</b></td>
                  <td>{c.pc}</td>
                  <td>{KIND[c.kind]}</td>
                  <td>{c.compliant}</td>
                  <td>{c.nonCompliant}</td>
                  <td>{c.unverifiable}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
    </div>
  );
}
